/**
 * SessionOrdersSheet — 세션 주문 시트 (#603 [3] → #654 N건 정합).
 *
 * 테이블 후불 세션의 미결제 주문 **전체(N건)** 의 라인을 바텀시트로 표시하고,
 * 합계와 "전체 결제하기" CTA([4] → #661 세션 결제 전용 페이지 진입)를 제공한다.
 *
 * ## #654: 주문별 섹션 표시
 *   기존 append 전제(Open 1건)로 단일 items 만 받던 것을 `orders`(주문별 구조)로 교체.
 *   - N건(≥2): 주문번호 라벨 섹션으로 구분해 전 주문 라인 표시 — 합계(서버 total_amount)와
 *     라인 내용이 정합한다(기존: orders[0] 라인만 표시 + N건 합산 합계 → 불일치).
 *   - 1건: 섹션 헤더 없이 기존과 동일한 평면 라인 나열 (시각 회귀 최소화).
 *
 * ## §0(C) 시간강조 없음
 *   `OrderItemDetail` 에 `created_at` 이 없어 라인 시간 정렬이 불가하다. 따라서 "방금"/`[NEW]`
 *   강조를 두지 않고(=BE-0 유지), 응답 순서(또는 menu_name) 그대로 나열한다.
 *   "방금" 강조는 [1] 주문완료 페이지의 delta 가 담당한다.
 *
 * ## 데이터 출처
 *   `useSessionUnpaidPostpay`(공유 훅)가 R5 `unpaid-postpay` 에서 구성한 `orders`/`totalAmount`.
 *   금액은 서버 권위 — FE 는 표시만 한다(재합산 금지).
 *
 * @see src/hooks/useSessionUnpaidPostpay.ts (데이터 출처)
 * @see src/pages/SessionChargePage.tsx ([4] → #661 전체 결제 전용 페이지 — onCharge 진입)
 * @see docs/plans/2026-06-04-postpay-table-order-ux-redesign-design.md §0(C)/§3 [3]
 */
import { useEffect } from 'react';
import { X } from 'lucide-react';
import type { OrderItemDetail } from '@/lib/api/types';
import { ordersHavePayableLines } from '@/hooks/useSessionUnpaidPostpay';
import type { SessionUnpaidOrder } from '@/hooks/useSessionUnpaidPostpay';
import type { Currency } from '@/types';
import { Z_INDEX } from '@/constants/zIndex';
import { formatCurrency } from '@/utils/currency';

export interface SessionOrdersSheetLabels {
  /** 시트 제목 (예: "우리 테이블 주문") */
  title: string;
  /** 합계 라벨 (예: "합계") */
  totalLabel: string;
  /** "전체 결제하기" CTA */
  payButton: string;
  /** 라인 0건 안내 */
  empty: string;
  /** 닫기 버튼 a11y 라벨 */
  closeLabel: string;
}

export interface SessionOrdersSheetProps {
  /** 시트 표시 여부 */
  isOpen: boolean;
  /**
   * 세션 미결제 주문 전체 (#654 — 주문별 라인 구조 보존).
   * N건(≥2)이면 주문번호 섹션으로 구분 표시, 1건이면 기존과 동일한 평면 나열.
   */
  orders: SessionUnpaidOrder[];
  /** 미결제 후불 합계 (서버 권위 문자열 — N건 합산) */
  totalAmount: string;
  /** 테이블 번호 (헤더 표시) */
  tableNumber?: string | null;
  /** 통화 (금액 포맷) */
  currency: Currency;
  /** i18n 라벨 */
  labels: SessionOrdersSheetLabels;
  /** 시트 닫기 */
  onClose: () => void;
  /** "전체 결제하기" — [4] → #661 세션 결제 전용 페이지 진입 */
  onCharge: () => void;
}

/** 선택 옵션 요약 — display_value 우선, 없으면 option_name 폴백. 빈 항목 제외. */
function summarizeOptions(item: OrderItemDetail): string {
  const opts = item.selected_options;
  if (!opts || opts.length === 0) return '';
  return opts
    .map((o) => o.display_value || o.option_name || '')
    .filter((v) => v.length > 0)
    .join(', ');
}

/**
 * 라인 1줄 (§0(C) — 시간강조 없이 응답 순서 그대로).
 *
 * #661: SessionChargePage(전용 결제 페이지)의 주문별 라인 렌더와 공유한다(렌더 패턴 단일 소스).
 */
export function SessionOrderLine({ item, currency }: { item: OrderItemDetail; currency: Currency }) {
  const optionSummary = summarizeOptions(item);
  return (
    <li
      data-testid={`session-sheet-line-${item.id}`}
      className="flex items-start justify-between gap-3"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">
          {item.menu_name}
          <span className="ml-1.5 text-muted-foreground">×{item.quantity}</span>
        </p>
        {optionSummary && (
          <p className="text-xs text-muted-foreground mt-0.5 break-words">
            {optionSummary}
          </p>
        )}
      </div>
      <span className="shrink-0 text-sm text-foreground tabular-nums">
        {formatCurrency(item.total_price, currency)}
      </span>
    </li>
  );
}

export function SessionOrdersSheet({
  isOpen,
  orders,
  totalAmount,
  tableNumber,
  currency,
  labels,
  onClose,
  onCharge,
}: SessionOrdersSheetProps) {
  // ESC 로 닫기 (a11y) — 열려 있을 때만 리스너 부착.
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // 서버 권위 합계 — 파싱 불가 시 0(표시 안전망). FE 재합산으로 대체하지 않는다.
  const numericTotal = Number(totalAmount);
  const totalDisplay = formatCurrency(
    Number.isFinite(numericTotal) ? numericTotal : 0,
    currency,
  );

  // #654 codex I3: 전 주문 라인 존재 여부 — 배너 CTA / 헤더 PAY 와 동일 판정 공유(SSOT).
  // 빈 주문만 있는 계약 위반 응답은 빈 안내 + CTA 숨김으로 수렴(fail-closed).
  const hasItems = ordersHavePayableLines(orders);
  // 1건이면 섹션 헤더 없이 기존 평면 나열 — 시각 회귀 최소화.
  const showOrderSections = orders.length > 1;

  return (
    <div
      // #634: 풀스크린 모달은 하단 네비(z-50) 위에 떠야 "전체 결제하기" 버튼이 가려지지 않는다.
      className="fixed inset-0 flex items-end justify-center"
      style={{ zIndex: Z_INDEX.SESSION_PAYMENT }}
      role="dialog"
      aria-modal="true"
      aria-label={labels.title}
    >
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/50"
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClose();
          }
        }}
        aria-label={labels.closeLabel}
      />

      {/* 바텀시트 컨텐츠 */}
      <div className="relative w-full max-w-md bg-background rounded-t-2xl max-h-[85vh] overflow-y-auto shadow-xl">
        {/* 헤더 */}
        <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h3 className="font-semibold text-foreground">{labels.title}</h3>
            {tableNumber && (
              <p className="text-sm text-muted-foreground">{tableNumber}</p>
            )}
          </div>
          <button
            type="button"
            data-testid="session-sheet-close"
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={labels.closeLabel}
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* 라인 목록 (#654 — N건이면 주문번호 섹션으로 구분, 1건이면 평면 나열) */}
          {hasItems ? (
            showOrderSections ? (
              <div className="space-y-4">
                {orders.map((order) => (
                  <section
                    key={order.id}
                    data-testid={`session-sheet-order-${order.id}`}
                    aria-label={order.orderNumber ?? `#${order.id}`}
                  >
                    {/* 주문번호 라벨 — 서버 order_number 그대로 (i18n 불요). null 이면 #id 폴백. */}
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      {order.orderNumber ?? `#${order.id}`}
                    </p>
                    <ul className="space-y-3">
                      {order.items.map((item) => (
                        <SessionOrderLine key={item.id} item={item} currency={currency} />
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            ) : (
              <ul className="space-y-3">
                {orders.flatMap((order) =>
                  order.items.map((item) => (
                    <SessionOrderLine key={item.id} item={item} currency={currency} />
                  )),
                )}
              </ul>
            )
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {labels.empty}
            </p>
          )}

          {/* 합계 (서버 권위) */}
          {hasItems && (
            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="text-sm font-medium text-foreground">
                {labels.totalLabel}
              </span>
              <span
                data-testid="session-sheet-total"
                className="text-base font-semibold text-foreground tabular-nums"
              >
                {totalDisplay}
              </span>
            </div>
          )}
        </div>

        {/* 전체 결제하기 CTA — [4] 진입 */}
        {hasItems && (
          <div className="sticky bottom-0 bg-background border-t border-border p-4">
            <button
              type="button"
              data-testid="session-sheet-pay"
              onClick={onCharge}
              className="w-full h-[52px] rounded-[8px] bg-primary text-white text-[16px] font-bold hover:bg-primary-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {labels.payButton}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SessionOrdersSheet;
